"use client";

import Image from "next/image";
import { ReactNode, useCallback, useRef } from "react";
import Badge from "~/componentsDS/Badge";
import { QuizCTA } from "~/components/features/quiz";
import Carousel from "~/componentsDS/Carousel";
import NextContainer from "~/componentsDS/Layout/Container";
import ElementorContainer from "~/componentsDS/ElementorLoyout/ElementorContainer";
import Heading from "~/componentsDS/Typography/Heading";
import Paragraph from "~/componentsDS/Typography/Paragraph";
import { CarouselApi, CarouselContent, CarouselItem } from "~/components/ui/carousel";
import { cn } from "~/lib/utils";
import { QUIZ_ROUTES, QuizRoute } from "~/constants/quizRoutes";
import Overline from "~/componentsDS/Typography/Overline";
import Reveal from "~/componentsDS/Reveal";
import { useTrackEvent } from "~/hooks/useTrackEvent";
import TextWithRegisteredMark from "~/componentsDS/TextWithRegisteredMark";

const ROW_LABELS = [
    "Treatment",
    "Liver\nmetabolism",
    "Dose\nrequired",
    "Hormone\nlevels",
    "Side\neffects",
    "Targeted\nsupport",
];

type Treatment = {
    highlighted: boolean;
    image: { src: string; alt: string; width: number; height: number };
    values: string[];
};

const TREATMENTS: Treatment[] = [
    {
        highlighted: true,
        image: {
            src: "https://res.cloudinary.com/dirscuuhp/image/upload/v1785314819/oestra_bxxe9d.webp",
            alt: "Oestra® vaginal cream by Inner Balance — bioidentical hormone delivery comparison chart",
            width: 141,
            height: 174,
        },
        values: [
            "Oestra® (Vaginal)",
            "Bypasses liver",
            "Lower, more efficient",
            "Steadier absorption",
            "Lower systemic burden",
            "Direct + systemic benefit",
        ],
    },
    {
        highlighted: false,
        image: {
            src: "https://res.cloudinary.com/dirscuuhp/image/upload/v1775555145/new_design/Oestra_Gemini_3_Nano_Banana_Pro__2026-02-26_02-27-04_1_2_lbkcnc.webp",
            alt: "Oral estrogen pill compared to Oestra cream for hormone delivery",
            width: 100,
            height: 119,
        },
        values: [
            "Oral",
            "Processed by liver",
            "Higher due to breakdown",
            "Peaks & Crashes",
            "Higher nausea & clotting markers",
            "Systemic only",
        ],
    },
    {
        highlighted: false,
        image: {
            src: "https://res.cloudinary.com/dirscuuhp/image/upload/v1775555145/new_design/Oestra_Gemini_3_Nano_Banana_Pro__2026-02-26_04-42-38_1_1_ekrype.webp",
            alt: "Estrogen patch — compared to Oestra vaginal cream for menopause hormone absorption",
            width: 110,
            height: 124,
        },
        values: [
            "Patch",
            "Bypasses liver",
            "Moderate",
            "Can fluctuate with skin factors",
            "Possible skin irritation",
            "Systemic only",
        ],
    },
];

const IMAGE_HEIGHT = "h-[150px] md:h-[140px]";
const ROW_HEIGHT = "h-[64px] md:h-[58px]";
const CTA_TEXT = "take the 2-min quiz";

type EventData = {
    event_name: string;
    widget: string;
}

type Props = {
    eventData?: EventData;
    headingElement?: ReactNode;
    sectionClassName?: string;
    useElementorContainer?: boolean;
    ctaLink?: QuizRoute;
}

const defaultHeaderElement = <Heading
    as="h2"
    size="sm"
    sizes={{ md: "md" }}
    className="text-primary500 mb-4 md:mb-3"
>
    Why{" "}
    <Heading
        as="i"
        font="featureDisplay"
        weight="400"
        className="text-[28px] md:text-[36px]"
    >
        delivery{" "}
    </Heading>
    method matters
</Heading>

const DeliverySection = ({
    eventData,
    sectionClassName,
    headingElement = defaultHeaderElement,
    useElementorContainer = false,
    ctaLink = QUIZ_ROUTES.HORMONE_IMBALANCE,
}: Props) => {
    const sendEvent = useTrackEvent();
    const prevIndexRef = useRef<number | null>(null);
    const LayoutContainer = useElementorContainer ? ElementorContainer : NextContainer;
    const wrapperProps = useElementorContainer
        ? {
            innerClassName: "flex-col",
        }
        : {
            className:
                "flex-col min-[1728px]:max-w-[1920px] min-[1728px]:px-[144px]",
        };

    const handleCarouselSelect = useCallback(
        (api: CarouselApi) => {
            if (!api || !eventData) return;

            const index = api.selectedScrollSnap();
            if (prevIndexRef.current === index) return;

            const treatment = TREATMENTS[index];
            prevIndexRef.current = index;

            if (!treatment) return;

            sendEvent({
                event: "user_interaction",
                value: {
                    event_name: eventData.event_name,
                    widget: eventData.widget,
                    meta_data: {
                        click_text: treatment.values[0],
                    },
                },
            });
        },
        [eventData, sendEvent]
    );

    return (
        <section className={cn("pb-16 md:pb-[96px]", sectionClassName)}>
            <LayoutContainer {...wrapperProps}>
                <Reveal>
                    <Badge className="mb-4 font-medium">How we compare</Badge>
                </Reveal>
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-2">
                    <div>
                        {headingElement}
                        <Paragraph size="lg" className="text-primary500 mb-8 md:mb-6">
                            How estrogen enters the body determines how well it works.
                        </Paragraph>
                    </div>
                    <QuizCTA
                        variant="primary"
                        href={ctaLink}
                        size="md"
                        className="hidden md:inline-flex shrink-0"
                        eventData={eventData ? {
                            event_name: eventData.event_name,
                            widget: eventData.widget,
                            meta_data: {
                                click_text: CTA_TEXT,
                            },
                        } : undefined}
                    >
                        {CTA_TEXT}
                    </QuizCTA>
                </div>

                <div className="mb-8 max-md:-mr-6">
                    <Carousel
                        variant="default"
                        loop={false}
                        opts={{ align: "start", skipSnaps: false }}
                        onSelect={handleCarouselSelect}
                    >
                        <div className="flex bg-gray100 rounded-3xl">
                            <div className="shrink-0 w-[150px] md:w-[244px] pt-6">
                                <div className={IMAGE_HEIGHT} />
                                {ROW_LABELS.map((label, idx) => (
                                    <div
                                        key={label}
                                        className={cn(
                                            ROW_HEIGHT,
                                            "flex items-center",
                                            "border-b-[0.63px] border-trueBlack/10",
                                            idx === 0 && "border-t-[0.63px] border-trueBlack/10"
                                        )}
                                    >
                                        <Overline weight="500" className="whitespace-pre-line text-primary500 pl-6 md:pl-12 pt-[3px]">{label}</Overline>
                                    </div>
                                ))}
                            </div>

                            <div className="flex-1 min-w-0">
                                <CarouselContent className="-ml-2 md:ml-0">
                                    {TREATMENTS.map((treatment, idx) => (
                                        <CarouselItem
                                            key={idx}
                                            className="pl-2 md:pl-0 basis-[214px] md:basis-1/3"
                                        >
                                            <div
                                                className={cn(
                                                    "relative flex flex-col items-center rounded-2xl pb-12",
                                                    treatment.highlighted
                                                        ? "pt-6 text-pureWhite"
                                                        : "mt-6 text-primary500"
                                                )}
                                            >
                                                {treatment.highlighted && (
                                                    <div className="absolute top-0 bottom-0 left-0 right-0 md:left-[44px] md:right-[44px] rounded-2xl
                                                        bg-[linear-gradient(180deg,_#19223D_2.38%,_#2A3966_28.94%,_#A9B9E5_117.19%)]
                                                        shadow-md
                                                        z-[-1]"
                                                    />
                                                )}
                                                <Reveal>
                                                    <div
                                                        className={cn(
                                                            IMAGE_HEIGHT,
                                                            "flex items-center justify-center w-full"
                                                        )}
                                                    >
                                                        <Image
                                                            src={treatment.image.src}
                                                            alt={treatment.image.alt}
                                                            width={treatment.image.width}
                                                            height={treatment.image.height}
                                                            quality={95}
                                                            className="object-contain max-h-full"
                                                        />
                                                    </div>
                                                </Reveal>
                                                {treatment.values.map((value, vIdx) => (
                                                    <div
                                                        key={vIdx}
                                                        className={cn(
                                                            ROW_HEIGHT,
                                                            "flex items-center justify-center text-center px-1 md:px-2 w-full",
                                                            "border-b-[0.63px] border-trueBlack/10",
                                                            vIdx === 0 && "border-t-[0.63px] border-trueBlack/10"
                                                        )}
                                                    >
                                                        <Paragraph weight={vIdx === 0 ? "500" : "400"}>
                                                            <TextWithRegisteredMark text={value} />
                                                        </Paragraph>
                                                    </div>
                                                ))}
                                            </div>
                                        </CarouselItem>
                                    ))}
                                </CarouselContent>
                            </div>
                        </div>
                    </Carousel>
                </div>

                <Paragraph size="lg" weight="500" className=" mb-6 text-primary400 md:text-center">
                    Oestra<sup>®</sup> works with your physiology —<br className="md:hidden"></br> not against it.
                </Paragraph>
                <Paragraph size="lg" className="text-primary400 md:text-center">
                    No digestive breakdown. No first-pass liver metabolism. <br className="max-md:hidden" />
                    Just efficient, steady estrogen support where it matters most.
                </Paragraph>

                <QuizCTA
                    variant="primary"
                    eventData={eventData ? {
                        event_name: eventData.event_name,
                        widget: eventData.widget,
                        meta_data: {
                            click_text: CTA_TEXT,
                        },
                    } : undefined}
                    href={ctaLink}
                    size="md"
                    className="md:hidden w-full mt-6"
                >
                    {CTA_TEXT}
                </QuizCTA>
            </LayoutContainer>
        </section>
    );
};

export default DeliverySection;
